import { useAuth } from '../contexts/auth-context';
import { useTrackConfig } from '../contexts/track-config-context';
import { useSeasonPassings } from '../hooks/useApi';
import { Button, Icon } from './ui';
import { fmtDateShort, fmtTime } from '../utils/format';
import { currentSeasonYear, getSeasonRange } from '../utils/lapCalc';
import { timedSessions } from '../utils/activityOverview';
import { BaanWelcome, BaanPersonalShortcut } from './BaanWelcome';

function SetupGuide({ transponders, onNavigate }) {
  const steps = [
    { title: 'Account', detail: 'Je profiel is klaar.', complete: true },
    {
      title: 'Transponder',
      detail: transponders.length ? `${transponders.length} gekoppeld` : 'Koppel je transponder om je ritten te vinden.',
      complete: transponders.length > 0,
    },
    { title: 'Hartslag', detail: 'Optioneel: koppel een sporthorloge of sensor.', optional: true },
  ];

  return (
    <section className="personal-setup" aria-labelledby="personal-setup-title">
      <div className="personal-setup-art" aria-hidden="true">
        <svg viewBox="0 0 360 150" fill="none">
          <path d="M56 42h248a34 34 0 0 1 0 68H56a34 34 0 0 1 0-68Z" />
          <path d="M56 52h248a24 24 0 0 1 0 48H56a24 24 0 0 1 0-48Z" />
          <circle cx="92" cy="42" r="4" />
          <circle cx="303" cy="76" r="4" />
        </svg>
      </div>
      <div className="personal-setup-copy">
        <p className="personal-dashboard-eyebrow">JOUW BAAN · JOUW DATA</p>
        <h2 id="personal-setup-title">Haal meer uit elke ronde.</h2>
        <p>Koppel je transponder. Je ritten en persoonlijke voortgang staan daarna vanzelf bij elkaar.</p>
        <ol className="personal-setup-steps">
          {steps.map((step, index) => (
            <li key={step.title} className={step.complete ? 'is-complete' : ''}>
              <span className="personal-step-number">
                {step.complete ? <Icon name="check" size={14} /> : `0${index + 1}`}
              </span>
              <span><strong>{step.title}</strong><small>{step.detail}</small></span>
              {step.optional && <em>OPTIONEEL</em>}
            </li>
          ))}
        </ol>
        <Button variant="accent" onClick={() => onNavigate('profile')}>
          {transponders.length ? 'Koppelingen beheren' : 'Transponder koppelen'}
          <Icon name="chevronRight" size={16} />
        </Button>
      </div>
      <div className="personal-setup-caption"><span className="live-dot" /> DE MEENT · ALKMAAR</div>
    </section>
  );
}

function MetricCard({ label, value, unit, icon, tone, note }) {
  return (
    <article className="personal-metric">
      <div className="personal-metric-top">
        <span>{label}</span>
        <i className={`personal-metric-icon ${tone}`}><Icon name={icon} size={17} /></i>
      </div>
      <strong>{value}<small>{unit}</small></strong>
      <p>{note}</p>
    </article>
  );
}

export default function PersonalDashboard({
  sessions,
  date,
  onNavigate,
}) {
  const { loggedIn, loading: authLoading, user, transponders, preferences } = useAuth();
  const cfg = useTrackConfig();
  const season = getSeasonRange(currentSeasonYear());
  const transponderIds = transponders.map(item => item.transponder);
  const { data: seasonData, loading: seasonLoading, error: seasonError } =
    useSeasonPassings(transponderIds.join(',') || null, season.from, season.to);

  if (!loggedIn || authLoading) {
    return (
      <>
        <BaanWelcome onNavigate={onNavigate} />
        {!authLoading && <BaanPersonalShortcut sessions={sessions} date={date} onNavigate={onNavigate} />}
      </>
    );
  }

  const seasonSessions = timedSessions(
    seasonData?.passings,
    seasonData?.pauseOverrides,
    cfg,
    preferences?.defaultLane ?? 'binnenbaan',
    (preferences?.pauseThresholdSeconds ?? 70) * 1000,
  );
  const completedSessions = seasonSessions.filter(session => session.laps.length > 0);
  const trainingDays = new Set(completedSessions.map(session => session.day)).size;
  const seasonLaps = completedSessions.reduce((total, session) => total + session.laps.length, 0);
  const seasonDistanceKm = completedSessions.reduce((total, session) => total + session.distanceKm, 0);
  const seasonBestMs = completedSessions.reduce(
    (best, session) => session.stats?.bestLapMs && (!best || session.stats.bestLapMs < best)
      ? session.stats.bestLapMs
      : best,
    null,
  );
  const latestSeasonSession = completedSessions[0];
  const greetingName = user?.name?.trim()?.split(/\s+/)[0] || 'schaatser';
  const seasonStatus = seasonError
    ? 'Seizoensgegevens niet beschikbaar'
    : seasonLoading
      ? 'Seizoensgegevens ophalen…'
      : `${season.label} · ${transponderIds.length} ${transponderIds.length === 1 ? 'transponder' : 'transponders'}`;

  return (
    <section className="personal-dashboard" aria-labelledby="personal-dashboard-title">
      <div className="personal-dashboard-heading">
        <div>
          <p className="personal-dashboard-eyebrow">
            `${season.label} · DE MEENT`
          </p>
          <h1 id="personal-dashboard-title">Welkom terug, {greetingName}</h1>
          <p className="personal-dashboard-subtitle">
            {seasonLaps
              ? `Je voortgang in schaatsseizoen ${season.label}.`
              : 'Volg je ritten en vooruitgang op De Meent.'}
          </p>
        </div>
        <button className="personal-dashboard-refresh" onClick={() => onNavigate('myseason')} aria-label="Bekijk alle eigen ritten">
          <Icon name="chevronRight" size={18} />
        </button>
      </div>

      {transponders.length === 0 ? (
        <SetupGuide transponders={transponders} onNavigate={onNavigate} />
      ) : (
        <>
          <div className="personal-dashboard-banner">
            <div className="personal-dashboard-banner-copy">
              <span><i className="live-dot" /> DE MEENT · ALKMAAR</span>
              <h2>{seasonLaps ? 'Jouw seizoen in beweging.' : 'Klaar voor je volgende rit?'}</h2>
              <p>{seasonLaps ? seasonStatus : 'Zodra je rijdt, verschijnen je rondes hier automatisch.'}</p>
              <button onClick={() => onNavigate('myseason')}>
                {seasonLaps ? 'Bekijk mijn ritten' : 'Bekijk mijn seizoen'}
                <Icon name="chevronRight" size={16} />
              </button>
            </div>
            <div className="personal-dashboard-track" aria-hidden="true">
              <svg viewBox="0 0 360 150" fill="none">
                <path d="M56 42h248a34 34 0 0 1 0 68H56a34 34 0 0 1 0-68Z" />
                <path d="M56 52h248a24 24 0 0 1 0 48H56a24 24 0 0 1 0-48Z" />
                <path d="M56 32h248a44 44 0 0 1 0 88H56a44 44 0 0 1 0-88Z" />
                <circle cx="92" cy="42" r="4" />
                <circle cx="303" cy="76" r="4" />
              </svg>
            </div>
          </div>

          <div className="personal-dashboard-section-title">
            <div><h2>Jouw seizoen</h2><span>{season.label}</span></div>
            <span className={`personal-freshness ${seasonError ? 'has-error' : ''}`} role={seasonError ? 'status' : undefined}>
              <i />{seasonStatus}
            </span>
          </div>
          <div className="personal-metrics">
            <MetricCard
              label="SCHAATSDAGEN"
              value={seasonLoading || seasonError ? '—' : trainingDays}
              unit=""
              icon="clock"
              tone="ice"
              note={seasonLoading ? 'seizoensdata laden…' : seasonError ? 'probeer later opnieuw' : 'met minstens één volledige ronde'}
            />
            <MetricCard
              label="RONDEN"
              value={seasonLoading || seasonError ? '—' : seasonLaps}
              unit=""
              icon="route"
              tone="blue"
              note={seasonLoading ? 'seizoensdata laden…' : seasonError ? 'probeer later opnieuw' : 'dit seizoen'}
            />
            <MetricCard
              label="AFSTAND"
              value={seasonLoading || seasonError ? '—' : seasonDistanceKm.toFixed(1)}
              unit={seasonLoading || seasonError ? '' : ' km'}
              icon="route"
              tone="blue"
              note={seasonLoading ? 'seizoensdata laden…' : seasonError ? 'probeer later opnieuw' : 'op basis van gemeten rondes'}
            />
            <MetricCard
              label="SEIZOENSBESTE"
              value={seasonLoading || seasonError ? '—' : seasonBestMs ? fmtTime(seasonBestMs) : '—'}
              unit={!seasonLoading && !seasonError && seasonBestMs ? ' s' : ''}
              icon="trophy"
              tone="gold"
              note={seasonLoading ? 'seizoensdata laden…' : seasonError ? 'probeer later opnieuw' : 'snelste volledige ronde'}
            />
          </div>
        </>
      )}
      {latestSeasonSession && !seasonError && (
        <div className="personal-ride-strip">
          <div className="personal-ride-date"><b>{latestSeasonSession.day.slice(8, 10)}</b><span>{latestSeasonSession.day.slice(5, 7)}</span></div>
          <div className="personal-ride-copy"><strong>Je laatste rit</strong><span>{fmtDateShort(latestSeasonSession.day)} · {latestSeasonSession.laps.length} ronden</span></div>
          {latestSeasonSession.stats?.bestLapMs && <div className="personal-ride-best"><span>BESTE RONDE</span><b>{fmtTime(latestSeasonSession.stats.bestLapMs)} <small>s</small></b></div>}
          <button onClick={() => {
            onNavigate('transponder', { transponder: latestSeasonSession.transponder, date: latestSeasonSession.day });
          }} aria-label="Open je rit"><Icon name="chevronRight" size={18} /></button>
        </div>
      )}
    </section>
  );
}
